import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

// ─── Helpers ────────────────────────────────────────────────────────────────

const lighten = (hex, amount = 0.4) => {
  hex = hex.replace("#", "");
  if (hex.length === 8) hex = hex.slice(0, 6);
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.min(255, r + Math.round((255 - r) * amount));
  g = Math.min(255, g + Math.round((255 - g) * amount));
  b = Math.min(255, b + Math.round((255 - b) * amount));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

// ─── Section order defaults ──────────────────────────────────────────────────

const defaultLeftSectionOrder = ["contact", "skills", "education", "languages"];

const defaultRightSectionOrder = [
  "about",
  "workExperience",
  "accomplishments",
  "certifications",
  "softwareSkills",
  "volunteering",
  "interests",
  "websites",
  "references",
  "additionalInfo"
];

// ─── Page size map ───────────────────────────────────────────────────────────

const pageSizeMap = {
  A4:        { width: "794px",  minHeight: "1123px" },
  Letter:    { width: "816px",  minHeight: "1056px" },
  Legal:     { width: "816px",  minHeight: "1344px" },
  A3:        { width: "1123px", minHeight: "1587px" },
  Executive: { width: "696px",  minHeight: "1008px" }
};

// ─── Resume17 Component ──────────────────────────────────────────────────────

const Resume17 = ({
  // Colors
  color = "#3f51b5",
  nameColor = "#ffffff",
  sidebarBackground,
  headerBackground,

  // Typography — DesignFormattingComponent sends actual CSS values (e.g. "14px", "Arial")
  font = "Arial, sans-serif",
  fontSize = "14px",          // actual px string from DesignFormattingComponent
  fontStyle = "normal",
  headingSize = "24px",       // actual px string from DesignFormattingComponent

  // Spacing — all numeric pixels from sliders
  lineSpacing = 20,           // slider value (10-50) — used as line-height in px
  sectionSpacing = 10,        // slider value (10-100)
  paragraphSpacing = 10,      // slider value (10-80)
  topBottomMargin = 20,       // slider value (10-100) — padding top/bottom of columns
  sideMargins = 20,           // slider value (10-100) — padding left/right of columns
  paragraphIndent = 0,
  lineWeight = 1,

  // Page
  pageSize = "A4",

  // Theme / data
  theme,
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true
}) => {

  // ── Colour palette ──────────────────────────────────────────────────────────
  const primaryColor = theme?.primary || color;
  const effectiveSidebarBackground = sidebarBackground || lighten(primaryColor, 0.95);
  const effectiveHeaderBackground   = headerBackground  || `linear-gradient(135deg, ${primaryColor}, ${lighten(primaryColor, 0.4)})`;
  const effectiveNameColor          = nameColor         || "#ffffff";

  const palette = {
    mainBackground:    "#FFFFFF",
    sidebarBackground: effectiveSidebarBackground,
    accentColor:       primaryColor,
    textColor:         "#333333",
    lightText:         "#666666",
    header:            effectiveHeaderBackground,
    nameColor:         effectiveNameColor
  };

  // ── Page dimensions ─────────────────────────────────────────────────────────
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  // ── Default resume data ─────────────────────────────────────────────────────
  const defaultData = {
    firstName:       "John",
    lastName:        "Doe",
    currentPosition: "Sales Representative",
    phone:           "+1 456-789-0123",
    email:           "hello@email.com",
    city:            "Anywhere St, Anytown",
    linkedIn:        "",
    profile:         "I am a Sales Representative with extensive experience in the fast-moving consumer goods industry."
  };
  const data = { ...defaultData, ...formData };

  // ── Typography helpers ──────────────────────────────────────────────────────
  // font: DesignFormattingComponent sends the font name as a plain string (e.g. "Arial", "EB Garamond")
  // We use it directly — if it's a web-safe font it works, for Google Fonts the parent must load them.
  const getFontFamily = () => {
    const safeFallbacks = {
      arial:      "Arial, sans-serif",
      helvetica:  "Helvetica, Arial, sans-serif",
      times:      "Times New Roman, serif",
      georgia:    "Georgia, serif",
      verdana:    "Verdana, Geneva, sans-serif",
      trebuchet:  "Trebuchet MS, sans-serif",
      courier:    "Courier New, monospace"
    };
    const key = (font || "").toLowerCase().replace(/\s+/g, "");
    return safeFallbacks[key] || `${font}, sans-serif`;
  };

  // fontSize comes as a px string ("14px") or a preset key ("normal")
  const getFontSize = () => {
    const presetMap = { small: "12px", normal: "14px", medium: "16px", large: "18px" };
    if (presetMap[fontSize]) return presetMap[fontSize];
    return fontSize || "14px";
  };

  // headingSize comes as a px string ("24px")
  const getHeadingSize = () => headingSize || "24px";

  // subheading = ~80% of headingSize
  const getSubheadingSize = () => {
    const base = parseInt(getHeadingSize(), 10);
    return `${Math.round(base * 0.75)}px`;
  };

  // lineSpacing comes as a numeric slider value (10–50), used directly as px line-height
  const getLineHeight = () => `${lineSpacing}px`;

  // ── Shared style objects ────────────────────────────────────────────────────
  const baseTextStyles = {
    fontFamily:  getFontFamily(),
    fontSize:    getFontSize(),
    fontStyle:   fontStyle || "normal",
    lineHeight:  getLineHeight()
  };

  const headingStyles = {
    ...baseTextStyles,
    fontSize:   getHeadingSize(),
    fontWeight: "bold"
  };

  const subheadingStyles = {
    ...baseTextStyles,
    fontSize:   getSubheadingSize(),
    fontWeight: "bold"
  };

  // ── State ───────────────────────────────────────────────────────────────────
  const [currentPage,           setCurrentPage]           = useState(0);
  const [pages,                 setPages]                 = useState([]);
  const [isDataLoaded,          setIsDataLoaded]          = useState(false);
  const mainContentRef    = useRef(null);
  const sidebarContentRef = useRef(null);

  const [educationEntries,      setEducationEntries]      = useState([]);
  const [savedSkills,           setSavedSkills]           = useState([]);
  const [savedSummary,          setSavedSummary]          = useState("");
  const [savedAdditionalInfo,   setSavedAdditionalInfo]   = useState([]);
  const [savedLanguages,        setSavedLanguages]        = useState([]);
  const [savedAccomplishments,  setSavedAccomplishments]  = useState([]);
  const [savedCertifications,   setSavedCertifications]   = useState([]);
  const [savedReferences,       setSavedReferences]       = useState([]);
  const [softwareSkills,        setSoftwareSkills]        = useState([]);
  const [savedVolunteering,     setSavedVolunteering]     = useState([]);
  const [savedInterests,        setSavedInterests]        = useState([]);
  const [savedWebsites,         setSavedWebsites]         = useState([]);
  const [customSections,        setCustomSections]        = useState({});

  const [leftSectionOrder,   setLeftSectionOrder]   = useState(defaultLeftSectionOrder);
  const [rightSectionOrder,  setRightSectionOrder]  = useState(defaultRightSectionOrder);
  const [dragOverIndex,      setDragOverIndex]      = useState(null);
  const [dragOverSide,       setDragOverSide]       = useState(null);
  const [arrangeDialogOpen,  setArrangeDialogOpen]  = useState(false);

  // ── Load data from localStorage ─────────────────────────────────────────────
  const loadAllData = useCallback(() => {
    try {
      const storedEntries = localStorage.getItem("educationEntries");
      if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

      const storedSkills = localStorage.getItem("skills");
      if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

      const storedSummaries = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")     || "[]"));
      setSavedLanguages(      JSON.parse(localStorage.getItem("languagesList")      || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList") || "[]"));
      setSavedCertifications( JSON.parse(localStorage.getItem("certificationsList") || "[]"));
      setSavedReferences(     JSON.parse(localStorage.getItem("referencesList")     || "[]"));
      setSoftwareSkills(      JSON.parse(localStorage.getItem("softwareSkills")     || "[]"));
      setSavedVolunteering(   JSON.parse(localStorage.getItem("volunteering")       || "[]"));
      setSavedInterests(      JSON.parse(localStorage.getItem("interests")          || "[]"));
      setSavedWebsites(       JSON.parse(localStorage.getItem("websites")           || "[]"));

      // Custom sections
      const userId = localStorage.getItem("userId");
      let sectionsData = {};
      if (userId) {
        const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
        currentCustomSections.forEach(sectionName => {
          try {
            const raw = localStorage.getItem(`custom_${userId}_${sectionName}`);
            if (raw) {
              const parsed     = JSON.parse(raw);
              const validItems = (parsed.items || []).filter(i => i && i.trim() !== "");
              if (validItems.length > 0) sectionsData[sectionName] = validItems;
            }
          } catch {}
        });
      }
      setCustomSections(sectionsData);

      // Section ordering
      const savedLeft = localStorage.getItem("resume17LeftSectionOrder");
      if (savedLeft) {
        setLeftSectionOrder(JSON.parse(savedLeft).filter(s => !s.startsWith("custom_")));
      } else {
        setLeftSectionOrder(defaultLeftSectionOrder);
      }

      const savedRight = localStorage.getItem("resume17RightSectionOrder");
      if (savedRight) {
        const customIds    = Object.keys(sectionsData).map(n => `custom_${n}`);
        const filtered     = JSON.parse(savedRight).filter(s => !s.startsWith("custom_"));
        setRightSectionOrder([...filtered, ...customIds]);
      } else {
        const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
        setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
      }

      setIsDataLoaded(true);
    } catch {
      setIsDataLoaded(true);
    }
  }, []);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  // Sync custom section ids into rightSectionOrder
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    const customIds    = Object.keys(customSections).map(n => `custom_${n}`);
    const filteredRight = rightSectionOrder.filter(s => !s.startsWith("custom_"));
    const newRight      = [...filteredRight, ...customIds];
    if (JSON.stringify(newRight) !== JSON.stringify(rightSectionOrder)) {
      setRightSectionOrder(newRight);
      localStorage.setItem("resume17RightSectionOrder", JSON.stringify(newRight));
    }
    const filteredLeft = leftSectionOrder.filter(s => !s.startsWith("custom_"));
    if (JSON.stringify(filteredLeft) !== JSON.stringify(leftSectionOrder)) {
      setLeftSectionOrder(filteredLeft);
      localStorage.setItem("resume17LeftSectionOrder", JSON.stringify(filteredLeft));
    }
  }, [customSections]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (leftSectionOrder.length > 0)
      localStorage.setItem("resume17LeftSectionOrder", JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0)
      localStorage.setItem("resume17RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  // ── Utilities ───────────────────────────────────────────────────────────────
  const formatDate = (month, year) => (month && year ? `${month} ${year}` : "");

  // ── Drag-and-drop handlers ──────────────────────────────────────────────────
  const handleLeftDragStart  = (e, index) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ index, side: "left",  sectionId: leftSectionOrder[index]  }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ index, side: "right", sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragOver  = (e, index, side) => { e.preventDefault(); setDragOverIndex(index); setDragOverSide(side); };
  const handleDragLeave = (e)              => { e.preventDefault(); setDragOverIndex(null);  setDragOverSide(null); };
  const handleDragEnd   = (e)              => { setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity = "1"; };

  const handleLeftDrop  = (e, targetIndex) => {
    e.preventDefault();
    const { index: src, side, sectionId } = JSON.parse(e.dataTransfer.getData("text/plain"));
    if (side === "left" && src !== targetIndex) {
      const o = [...leftSectionOrder]; const [m] = o.splice(src, 1); o.splice(targetIndex, 0, m); setLeftSectionOrder(o);
    } else if (side === "right") {
      const l = [...leftSectionOrder]; const r = [...rightSectionOrder];
      r.splice(src, 1); l.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(l); setRightSectionOrder(r);
    }
    setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity = "1";
  };
  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const { index: src, side, sectionId } = JSON.parse(e.dataTransfer.getData("text/plain"));
    if (side === "right" && src !== targetIndex) {
      const o = [...rightSectionOrder]; const [m] = o.splice(src, 1); o.splice(targetIndex, 0, m); setRightSectionOrder(o);
    } else if (side === "left") {
      const l = [...leftSectionOrder]; const r = [...rightSectionOrder];
      l.splice(src, 1); r.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(l); setRightSectionOrder(r);
    }
    setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity = "1";
  };

  const resetLeftSectionOrder  = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => { const ids = Object.keys(customSections).map(n => `custom_${n}`); setRightSectionOrder([...defaultRightSectionOrder, ...ids]); };
  const resetAllSectionOrder   = () => { resetLeftSectionOrder(); resetRightSectionOrder(); };

  const getSectionTitle = (id) => {
    const titles = {
      contact: "Contact", skills: "Skills", education: "Education", languages: "Languages",
      about: "About Me", workExperience: "Work Experience", accomplishments: "Accomplishments",
      certifications: "Certifications", softwareSkills: "Software Skills", volunteering: "Volunteering",
      interests: "Interests", websites: "Websites/Profiles", references: "References", additionalInfo: "Additional Information"
    };
    if (id.startsWith("custom_")) {
      const n = id.replace("custom_", "").replace(/_/g, " ");
      return n.charAt(0).toUpperCase() + n.slice(1);
    }
    return titles[id] || id;
  };

  // ── Drag panel (dialog) ─────────────────────────────────────────────────────
  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", ...baseTextStyles }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === "left" ? "Left Sidebar" : "Right Content"} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1, ...baseTextStyles }}>
          ({sections.length} sections)
        </Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => side === "left" ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side === "left" ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: "flex", alignItems: "center",
              backgroundColor: dragOverIndex === index && dragOverSide === side ? "#f0f0f0" : "white",
              boxShadow:       dragOverIndex === index && dragOverSide === side ? 2 : 1,
              cursor: "grab", "&:active": { cursor: "grabbing" }, transition: "all 0.2s ease"
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1, ...baseTextStyles }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ ...baseTextStyles }}>{index + 1}</Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: "center", ...baseTextStyles }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
      <Button onClick={side === "left" ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset {side === "left" ? "Left" : "Right"} Order
      </Button>
    </Box>
  );

  // ── Pagination ──────────────────────────────────────────────────────────────
  const paginateContent = useCallback(() => {
    if (!mainContentRef.current || !sidebarContentRef.current || !isDataLoaded) return;

    const mainSections    = Array.from(mainContentRef.current.querySelectorAll(".resume-section"));
    const sidebarSections = Array.from(sidebarContentRef.current.querySelectorAll(".sidebar-section"));

    const pageHeightPx          = parseInt(page.minHeight, 10);
    const headerHeight          = 200 + (topBottomMargin * 0.5); // account for header + gap
    const availableFirstPage    = pageHeightPx - headerHeight - (topBottomMargin * 2);
    const availableOtherPages   = pageHeightPx - (topBottomMargin * 2);

    let newPages   = [];
    let isFirstPage = true;
    let mainIdx    = 0;
    let sidebarIdx = 0;

    if (mainSections.length === 0 && sidebarSections.length === 0) {
      setPages([{ main: [], sidebar: [], hasHeader: true }]);
      return;
    }

    while (mainIdx < mainSections.length || sidebarIdx < sidebarSections.length) {
      const avail = isFirstPage ? availableFirstPage : availableOtherPages;
      const pageMain    = [];
      const pageSidebar = [];
      let mainH    = 0;
      let sidebarH = 0;

      while (mainIdx < mainSections.length) {
        const s = mainSections[mainIdx];
        const h = s.offsetHeight;
        if (mainH + h > avail) {
          if (pageMain.length === 0) { pageMain.push(s); mainH += h; mainIdx++; }
          break;
        }
        pageMain.push(s); mainH += h; mainIdx++;
      }

      while (sidebarIdx < sidebarSections.length) {
        const s = sidebarSections[sidebarIdx];
        const h = s.offsetHeight;
        if (sidebarH + h > avail) {
          if (pageSidebar.length === 0) { pageSidebar.push(s); sidebarH += h; sidebarIdx++; }
          break;
        }
        pageSidebar.push(s); sidebarH += h; sidebarIdx++;
      }

      newPages.push({ main: [...pageMain], sidebar: [...pageSidebar], hasHeader: isFirstPage });
      isFirstPage = false;
    }

    if (newPages.length === 0) {
      newPages.push({ main: [...mainSections], sidebar: [...sidebarSections], hasHeader: true });
    }

    setPages(newPages);
  }, [
    page.minHeight, topBottomMargin, isDataLoaded,
    leftSectionOrder, rightSectionOrder,
    font, fontSize, fontStyle, lineSpacing, headingSize,
    sectionSpacing, paragraphSpacing, sideMargins, paragraphIndent, lineWeight
  ]);

  useEffect(() => {
    if (!isDataLoaded) return;
    const id = setTimeout(() => paginateContent(), 800);
    return () => clearTimeout(id);
  }, [
    paginateContent, isDataLoaded,
    educationEntries, savedSkills, savedSummary, workExperiences,
    savedAdditionalInfo, savedLanguages, savedAccomplishments,
    savedCertifications, softwareSkills, savedVolunteering,
    savedInterests, savedWebsites, customSections,
    leftSectionOrder, rightSectionOrder,
    color, font, fontSize, fontStyle, lineSpacing, headingSize,
    sectionSpacing, paragraphSpacing, topBottomMargin, sideMargins, paragraphIndent, lineWeight, pageSize
  ]);

  // ── Section renderers ───────────────────────────────────────────────────────

  // sectionSpacing  → marginBottom between sections  (slider 10-100, used as px directly)
  // paragraphSpacing → marginBottom between paragraphs inside a section (slider 10-80, used as px directly)
  // paragraphIndent  → text-indent for "about" paragraph

  const renderSection = (sectionId, inSidebar = false) => {
    const textColor = palette.textColor;

    // Every section wrapper uses sectionSpacing directly as px
    const sectionStyle = {
      marginBottom:  `${sectionSpacing}px`,
      wordBreak:     "break-word",
      overflowWrap:  "break-word",
      ...baseTextStyles
    };

    // Items inside sections use paragraphSpacing directly as px
    const paragraphStyle = {
      marginBottom:  `${paragraphSpacing}px`,
      wordBreak:     "break-word",
      overflowWrap:  "break-word",
      ...baseTextStyles
    };

    switch (sectionId) {

      // ── Contact ─────────────────────────────────────────────────────────────
      case "contact":
        return (
          <div key="contact" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Contact
            </Typography>
            <List dense disablePadding>
              {data.phone && (
                <ListItem sx={{ px: 0, py: `${paragraphSpacing / 4}px` }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <PhoneIcon color="primary" sx={{ fontSize: getFontSize() }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={data.phone}
                    primaryTypographyProps={{ sx: { ...baseTextStyles, wordBreak: "break-all" } }}
                  />
                </ListItem>
              )}
              {data.email && (
                <ListItem sx={{ px: 0, py: `${paragraphSpacing / 4}px` }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <EmailIcon color="primary" sx={{ fontSize: getFontSize() }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={data.email}
                    primaryTypographyProps={{ sx: { ...baseTextStyles, wordBreak: "break-all" } }}
                  />
                </ListItem>
              )}
              {data.city && (
                <ListItem sx={{ px: 0, py: `${paragraphSpacing / 4}px` }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LocationOnIcon color="primary" sx={{ fontSize: getFontSize() }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={data.city}
                    primaryTypographyProps={{ sx: { ...baseTextStyles, wordBreak: "break-word" } }}
                  />
                </ListItem>
              )}
              {data.linkedIn && (
                <ListItem sx={{ px: 0, py: `${paragraphSpacing / 4}px` }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LinkedInIcon sx={{ fontSize: getFontSize(), color: "#0077b5" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <a
                        href={data.linkedIn.startsWith("http") ? data.linkedIn : `https://${data.linkedIn}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: textColor, textDecoration: "none", ...baseTextStyles, wordBreak: "break-all" }}
                      >
                        {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, "")}
                      </a>
                    }
                    primaryTypographyProps={{ sx: { ...baseTextStyles } }}
                  />
                </ListItem>
              )}
            </List>
          </div>
        );

      // ── Skills ───────────────────────────────────────────────────────────────
      case "skills":
        return savedSkills.length > 0 ? (
          <div key="skills" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Skills
            </Typography>
            {savedSkills.map((skill, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, color: textColor }}>• {skill.name}</Typography>
            ))}
          </div>
        ) : null;

      // ── Education ────────────────────────────────────────────────────────────
      case "education":
        return educationEntries.length > 0 ? (
          <div key="education" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Education
            </Typography>
            {educationEntries.map((edu, i) => {
              const isSchool   = edu.educationLevel === "school";
              const degree     = isSchool ? (edu.standard ? `${edu.standard} Standard` : "School") : edu.degreeName || "";
              const field      = isSchool ? edu.stream || "" : edu.specialization || edu.degreeType || "";
              const school     = isSchool ? edu.schoolName    || "" : edu.collegeName    || "";
              const location   = isSchool ? edu.schoolLocation|| "" : edu.collegeLocation|| "";
              const buildDesc  = () => {
                let d = "";
                if (isSchool) {
                  if (edu.district)    d += `District: ${edu.district}\n`;
                  if (edu.percentage)  d += `Percentage: ${edu.percentage}%\n`;
                } else {
                  if (edu.programType) d += `Program: ${edu.programType}\n`;
                  if (edu.cgpa)        d += `CGPA: ${edu.cgpa}\n`;
                  if (edu.grade)       d += `Grade: ${edu.grade}\n`;
                  if (edu.percentage)  d += `Percentage: ${edu.percentage}%\n`;
                }
                if (edu.additionalCoursework) d += edu.additionalCoursework;
                return d.trim();
              };
              return (
                <Box key={i} sx={{ ...paragraphStyle }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={0.5}>
                    <Typography sx={{ ...subheadingStyles, color: textColor, flex: "1 1 auto" }}>
                      {degree}{field && ` - ${field}`}
                    </Typography>
                    <Typography sx={{ ...baseTextStyles, color: palette.lightText, flexShrink: 0 }}>
                      {edu.gradMonth && edu.gradYear ? formatDate(edu.gradMonth, edu.gradYear) : ""}
                    </Typography>
                  </Box>
                  <Typography sx={{ ...baseTextStyles, color: palette.lightText, mb: `${paragraphSpacing / 2}px` }}>
                    {school}{location && `, ${location}`}
                  </Typography>
                  {(() => {
                    const desc = buildDesc();
                    if (!desc) return null;
                    return (
                      <Box
                        sx={{ "& strong": { fontWeight: "bold" }, "& em": { fontStyle: "italic" }, ...baseTextStyles, color: textColor }}
                        dangerouslySetInnerHTML={{ __html: desc.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\*(.*?)\*/g, "<em>$1</em>").replace(/<u>(.*?)<\/u>/g, "<u>$1</u>").replace(/\n/g, "<br/>") }}
                      />
                    );
                  })()}
                </Box>
              );
            })}
          </div>
        ) : null;

      // ── Languages ────────────────────────────────────────────────────────────
      case "languages":
        return savedLanguages.length > 0 ? (
          <div key="languages" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Languages
            </Typography>
            {savedLanguages.map((lang, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, color: textColor }}>
                {lang.name}{lang.level ? ` – ${lang.level}` : ""}
              </Typography>
            ))}
          </div>
        ) : null;

      // ── About ────────────────────────────────────────────────────────────────
      case "about":
        return savedSummary || data.profile ? (
          <div key="about" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              About Me
            </Typography>
            <Typography sx={{ ...paragraphStyle, color: textColor, textIndent: `${paragraphIndent}px` }}>
              {savedSummary || data.profile}
            </Typography>
          </div>
        ) : null;

      // ── Work Experience ──────────────────────────────────────────────────────
      case "workExperience":
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Work Experience
            </Typography>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ ...paragraphStyle }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={0.5}>
                  <Typography sx={{ ...subheadingStyles, color: textColor, flex: "1 1 auto" }}>
                    {work.jobTitle}
                  </Typography>
                  <Typography sx={{ ...baseTextStyles, color: palette.lightText, flexShrink: 0 }}>
                    {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                <Typography sx={{ ...baseTextStyles, color: palette.lightText, mb: `${paragraphSpacing / 2}px` }}>
                  {work.employer}
                </Typography>
                {work.description && (
                  <Box
                    sx={{ "& strong": { fontWeight: "bold" }, "& em": { fontStyle: "italic" }, ...baseTextStyles, color: textColor, wordBreak: "break-word" }}
                    dangerouslySetInnerHTML={{ __html: work.description.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\*(.*?)\*/g, "<em>$1</em>").replace(/<u>(.*?)<\/u>/g, "<u>$1</u>").replace(/\n/g, "<br/>") }}
                  />
                )}
              </Box>
            ))}
          </div>
        ) : null;

      // ── Accomplishments ──────────────────────────────────────────────────────
      case "accomplishments":
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Accomplishments
            </Typography>
            {savedAccomplishments.map((acc, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, whiteSpace: "pre-line", color: textColor }}>• {acc}</Typography>
            ))}
          </div>
        ) : null;

      // ── Certifications ───────────────────────────────────────────────────────
      case "certifications":
        return savedCertifications.length > 0 ? (
          <div key="certifications" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Certifications
            </Typography>
            {savedCertifications.map((cert, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, color: textColor }}>
                • {cert.name} – {cert.provider} ({cert.year})
              </Typography>
            ))}
          </div>
        ) : null;

      // ── Software Skills ──────────────────────────────────────────────────────
      case "softwareSkills":
        return softwareSkills.length > 0 ? (
          <div key="softwareSkills" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Software Skills
            </Typography>
            {softwareSkills.map((skill, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, color: textColor }}>• {skill.name} — {skill.level}%</Typography>
            ))}
          </div>
        ) : null;

      // ── Volunteering ─────────────────────────────────────────────────────────
      case "volunteering":
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Volunteering
            </Typography>
            {savedVolunteering.map((vol, i) => (
              <Box key={i} sx={{ ...paragraphStyle }}>
                <Typography sx={{ ...subheadingStyles, color: textColor }}>{vol.subtopic}</Typography>
                <Typography sx={{ ...baseTextStyles, color: palette.lightText }}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography sx={{ ...baseTextStyles, whiteSpace: "pre-line", mt: `${paragraphSpacing / 4}px`, color: textColor }}>{vol.content}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      // ── Interests ────────────────────────────────────────────────────────────
      case "interests":
        return savedInterests.length > 0 ? (
          <div key="interests" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Interests
            </Typography>
            {savedInterests.map((interest, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, color: textColor }}>• {interest}</Typography>
            ))}
          </div>
        ) : null;

      // ── Websites ─────────────────────────────────────────────────────────────
      case "websites":
        return savedWebsites.length > 0 ? (
          <div key="websites" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Websites / Profiles
            </Typography>
            {savedWebsites.map((site, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, color: textColor, wordBreak: "break-all" }}>
                •{" "}
                <a
                  href={site.url.startsWith("http") ? site.url : `https://${site.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: palette.accentColor, textDecoration: "none", ...baseTextStyles }}
                >
                  {site.name || site.url}
                </a>
                {site.addToHeader ? " (Shown in Header)" : ""}
              </Typography>
            ))}
          </div>
        ) : null;

      // ── References ───────────────────────────────────────────────────────────
      case "references":
        return savedReferences.length > 0 ? (
          <div key="references" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              References
            </Typography>
            {savedReferences.map((ref, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, whiteSpace: "pre-line", color: textColor }}>• {ref}</Typography>
            ))}
          </div>
        ) : null;

      // ── Additional Info ──────────────────────────────────────────────────────
      case "additionalInfo":
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
              Additional Information
            </Typography>
            {savedAdditionalInfo.map((info, i) => (
              <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, color: textColor }}>• {info}</Typography>
            ))}
          </div>
        ) : null;

      // ── Custom sections ──────────────────────────────────────────────────────
      default:
        if (sectionId && sectionId.startsWith("custom_")) {
          const name  = sectionId.replace("custom_", "");
          const items = customSections[name] || [];
          if (items.length > 0) {
            return (
              <div key={sectionId} className={inSidebar ? "sidebar-section" : "resume-section"} style={sectionStyle}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: palette.accentColor, mb: `${paragraphSpacing}px`, ...subheadingStyles }}>
                  {name.toUpperCase()}
                </Typography>
                {items.map((item, i) => (
                  <Typography key={i} sx={{ ...paragraphStyle, ...baseTextStyles, whiteSpace: "pre-line", color: textColor }}>• {item}</Typography>
                ))}
              </div>
            );
          }
        }
        return null;
    }
  };

  const renderSidebarSections  = () => leftSectionOrder.map(id  => renderSection(id, true)).filter(Boolean);
  const renderRightSections    = () => rightSectionOrder.map(id => renderSection(id, false)).filter(Boolean);

  // ── Hidden measurement box ──────────────────────────────────────────────────
  // Must match the exact widths used in the real page so measurements are accurate.
  const renderMeasurementBox = () => (
    <Box sx={{ visibility: "hidden", position: "absolute", left: "-9999px", top: 0, width: page.width, pointerEvents: "none" }}>
      {/* Sidebar column: 30% */}
      <Box
        ref={sidebarContentRef}
        sx={{
          width: "30%", float: "left", boxSizing: "border-box",
          p: `${topBottomMargin}px ${sideMargins}px`,
          ...baseTextStyles
        }}
      >
        {renderSidebarSections()}
      </Box>
      {/* Main column: 70% */}
      <Box
        ref={mainContentRef}
        sx={{
          width: "70%", float: "left", boxSizing: "border-box",
          p: `${topBottomMargin}px ${sideMargins}px`,
          ...baseTextStyles
        }}
      >
        {renderRightSections()}
      </Box>
    </Box>
  );

  // ── Sidebar column render (from paginated DOM nodes) ────────────────────────
  const renderSidebarColumn = (pageContent) => (
    <Box sx={{
      width: "30%",
      flexShrink: 0,
      bgcolor: palette.sidebarBackground,
      // topBottomMargin and sideMargins from sliders — used directly as px
      p: `${topBottomMargin}px ${sideMargins}px`,
      boxSizing: "border-box",
      borderRight: `${lineWeight}px solid #eee`,
      overflow: "hidden",
      minWidth: 0,
      ...baseTextStyles
    }}>
      {pageContent.sidebar.length > 0 && (
        <div
          style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
          dangerouslySetInnerHTML={{ __html: pageContent.sidebar.map(s => s.outerHTML).join("") }}
        />
      )}
    </Box>
  );

  // ── Main column render (from paginated DOM nodes) ───────────────────────────
  const renderMainColumn = (pageContent) => (
    <Box sx={{
      width: "70%",
      flexShrink: 0,
      // topBottomMargin and sideMargins from sliders — used directly as px
      p: `${topBottomMargin}px ${sideMargins}px`,
      boxSizing: "border-box",
      overflow: "hidden",
      minWidth: 0,
      ...baseTextStyles
    }}>
      {pageContent.main.length > 0 && (
        <div
          style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
          dangerouslySetInnerHTML={{ __html: pageContent.main.map(s => s.outerHTML).join("") }}
        />
      )}
    </Box>
  );

  // ── Header ──────────────────────────────────────────────────────────────────
  const renderHeader = (hasHeader) => {
    if (!hasHeader) return null;
    return (
      <Box sx={{
        width: "100%",
        height: "200px",
        background: palette.header,
        clipPath: "polygon(0 0, 100% 0, 100% 75%, 50% 100%, 0 75%)",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        color: "white",
        flexShrink: 0,
        overflow: "hidden"
      }}>
        <Box sx={{ textAlign: "center", px: 3, mt: 2, overflow: "hidden", maxWidth: "80%" }}>
          <Typography sx={{ fontWeight: "bold", color: palette.nameColor, wordBreak: "break-word", ...headingStyles, fontSize: getHeadingSize() }}>
            {data.firstName} {data.lastName}
          </Typography>
          <Typography sx={{ color: palette.nameColor, wordBreak: "break-word", ...subheadingStyles, mb: `${paragraphSpacing / 2}px`, mt: `${paragraphSpacing / 2}px` }}>
            {data.currentPosition}
          </Typography>
        </Box>
        <Avatar
          src={photo || "https://via.placeholder.com/150"}
          sx={{ width: 100, height: 100, border: "4px solid white", position: "absolute", bottom: "-3px", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}
        />
      </Box>
    );
  };

  // ── Loading state ────────────────────────────────────────────────────────────
  if (!isDataLoaded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px", ...baseTextStyles }}>
        <Typography sx={{ ...baseTextStyles }}>Loading resume data…</Typography>
      </Box>
    );
  }

  // ── Page box styles ──────────────────────────────────────────────────────────
  const pageBoxStyles = {
    width: page.width,
    minHeight: page.minHeight,
    mx: "auto",
    bgcolor: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
    position: "relative",
    pageBreakAfter: "always",
    "@media print": { width: page.width, minHeight: page.minHeight, boxShadow: "none", m: 0, overflow: "hidden" }
  };

  // ── Empty state fallback ─────────────────────────────────────────────────────
  const renderEmptyPage = () => (
    <Box className="resume-page" sx={{ ...pageBoxStyles, boxShadow: 3 }}>
      {renderHeader(true)}
      <Box sx={{ display: "flex", flex: 1, mt: `${paragraphSpacing}px`, overflow: "hidden", width: "100%", boxSizing: "border-box" }}>
        <Box sx={{ width: "30%", flexShrink: 0, bgcolor: palette.sidebarBackground, p: `${topBottomMargin}px ${sideMargins}px`, borderRight: `${lineWeight}px solid #eee`, boxSizing: "border-box", overflow: "hidden" }} />
        <Box sx={{ width: "70%", flexShrink: 0, p: `${topBottomMargin}px ${sideMargins}px`, boxSizing: "border-box", overflow: "hidden" }} />
      </Box>
    </Box>
  );

  // ── Main render ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", position: "relative", m: 5, ...baseTextStyles }}>

      {/* Navigation & controls */}
      {!exporting && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2, gap: 2, "@media print": { display: "none" }, ...baseTextStyles }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, ...baseTextStyles }}>Page {currentPage + 1} of {Math.max(1, pages.length)}</Typography>
          <IconButton onClick={() => setCurrentPage(p => Math.min(pages.length - 1, p + 1))} disabled={currentPage >= Math.max(0, pages.length - 1)} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", ml: 2 }}>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Hidden measurement nodes */}
      {renderMeasurementBox()}

      {/* Visible pages */}
      <Box id="resume-container">
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <Box
              key={index}
              className="resume-page"
              sx={{
                ...pageBoxStyles,
                boxShadow: !exporting && index === currentPage ? 3 : 0,
                display:   exporting ? "flex" : (index === currentPage ? "flex" : "none"),
                "@media print": { display: "flex" }
              }}
            >
              {renderHeader(pageContent.hasHeader)}
              <Box sx={{
                display: "flex",
                flex: 1,
                mt: pageContent.hasHeader ? `${paragraphSpacing}px` : 0,
                overflow: "hidden",
                minHeight: 0,
                width: "100%",
                boxSizing: "border-box"
              }}>
                {renderSidebarColumn(pageContent)}
                {renderMainColumn(pageContent)}
              </Box>
            </Box>
          ))
        ) : renderEmptyPage()}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, ...baseTextStyles }}>
            Drag and drop sections to reorder them or move between left sidebar and right content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1 }}>{renderDragPanel("left",  leftSectionOrder)}</Box>
            <Box sx={{ flex: 1 }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Meta ────────────────────────────────────────────────────────────────────

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: "#3f51b5", label: "Indigo"  },
    { value: "#2196f3", label: "Blue"    },
    { value: "#f44336", label: "Red"     },
    { value: "#4caf50", label: "Green"   },
    { value: "#ff9800", label: "Orange"  },
    { value: "#9c27b0", label: "Purple"  }
  ],
  nameColorOptions: [
    { value: "#f3ededff", label: "Light"     },
    { value: "#333333",   label: "Dark Gray" },
    { value: "#000000",   label: "Black"     },
    { value: "#1a2b47",   label: "Dark Blue" },
    { value: "#2e7d32",   label: "Green"     }
  ],
  sidebarBackgroundOptions: [
    { value: "#f5f5f5",   label: "Light Gray"     },
    { value: "#e6f3f9ff", label: "Light Blue"     },
    { value: "#f0f8e6ff", label: "Light Green"    },
    { value: "#f9e6f3ff", label: "Light Pink"     },
    { value: "#e6e6f9ff", label: "Light Lavender" },
    { value: "#f9f3e6ff", label: "Light Beige"    }
  ],
  headerBackgroundOptions: [
    { value: "linear-gradient(135deg, #3f51b5, #2196f3)", label: "Blue Gradient"        },
    { value: "linear-gradient(135deg, #f44336, #ff9800)", label: "Red-Orange Gradient"  },
    { value: "linear-gradient(135deg, #4caf50, #8bc34a)", label: "Green Gradient"       },
    { value: "linear-gradient(135deg, #9c27b0, #e91e63)", label: "Purple-Pink Gradient" },
    { value: "linear-gradient(135deg, #607d8b, #9e9e9e)", label: "Gray Gradient"        }
  ],
  fontFamilyOptions: [
    { value: "Arial",           label: "Arial"           },
    { value: "Times New Roman", label: "Times New Roman" },
    { value: "Helvetica",       label: "Helvetica"       },
    { value: "Georgia",         label: "Georgia"         },
    { value: "Verdana",         label: "Verdana"         },
    { value: "Trebuchet MS",    label: "Trebuchet MS"    },
    { value: "Courier New",     label: "Courier New"     },
    { value: "EB Garamond",     label: "EB Garamond"     },
    { value: "Roboto",          label: "Roboto"          },
    { value: "Poppins",         label: "Poppins"         },
    { value: "Open Sans",       label: "Open Sans"       },
    { value: "Lato",            label: "Lato"            },
    { value: "Montserrat",      label: "Montserrat"      },
    { value: "Raleway",         label: "Raleway"         },
    { value: "Playfair Display",label: "Playfair Display"},
    { value: "Merriweather",    label: "Merriweather"    },
    { value: "Nunito",          label: "Nunito"          },
    { value: "Oswald",          label: "Oswald"          },
    { value: "Ubuntu",          label: "Ubuntu"          }
  ],
  fontSizeOptions: [
    { value: "12px", label: "Small"  },
    { value: "14px", label: "Normal" },
    { value: "16px", label: "Medium" },
    { value: "18px", label: "Large"  }
  ],
  fontStyleOptions: [
    { value: "normal", label: "Normal" },
    { value: "italic", label: "Italic" }
  ],
  lineSpacingOptions: [
    { value: 14, label: "Tight"   },
    { value: 20, label: "Normal"  },
    { value: 26, label: "Relaxed" },
    { value: 32, label: "Loose"   }
  ],
  experienceLevel: ["No Experience", "Less than 3 years", "3-5 Years"]
};

export default Resume17;